<script setup lang="ts">
import {Pencil, ShoppingCartIcon} from "lucide-vue-next";
import {ref} from "vue";
import {toast} from "vue-sonner";
import {Money} from "../../../utils/money.ts";
import {label} from "../../mixins/translations.ts";
import {useCartStore} from "../../stores/cart.ts";
import {useConfigurationStore} from "../../stores/configuration.ts";
import Modal from "../Modal.vue";
import ConfigurationCategory from "./ConfigurationCategory.vue";
import ConfigurationLoadingSkeleton from "./ConfigurationLoadingSkeleton.vue";

interface Props {
    cart_line_id?: number
    currency: string
}
const props = defineProps<Props>();
const configurationStore = useConfigurationStore();
const cartStore = useCartStore();
const emit = defineEmits<(e: 'close') => void>();

const open = ref(false);

const requestEditConfiguration = () => {
    configurationStore.editConfiguration(props.cart_line_id, cartStore.uuid).then(()=> {
        open.value = true;
    });
}

const updateConfiguration = () => {
    configurationStore.validate().then(() => {
        if(!Object.keys(configurationStore.validationErrors).length) {
            configurationStore.updateConfiguration(props.cart_line_id, cartStore.uuid).then((response)=> {
                open.value = false;
                toast.success(response.data.message)
                cartStore.loadCart();
            });
        }
    });
}
</script>

<template>
    <Modal v-model:open="open" width-class="max-w-[700px] w-full min-h-screen md:min-h-auto">
        <template #trigger-wrapper>
            <button @click="requestEditConfiguration()"
                    type="button"
                    class="inline-flex h-9 w-9 items-center justify-center rounded-md bg-secondary-50 text-primary-500 transition hover:bg-slate-100"
                    aria-label="Product bewerken"
            >
                <Pencil class="h-4 w-4" />
            </button>
        </template>
        <template #title>{{ configurationStore.product?.label }}</template>
        <template #description>
            <configuration-category v-for="category in configurationStore.configuration.categories" :category="category" :key="category.id">
            </configuration-category>

            <ConfigurationLoadingSkeleton>
                <div class="mt-4 sticky bottom-6 bg-white py-2 md:static md:bottom-0 md:py-0">
                    <div class="text-sm font-bold text-secondary-700">
                        {{ label('site.product.total-incl-options') }}
                    </div>
                    <div class="mt-2 flex items-end gap-2">
                        <span class="text-2xl font-semibold leading-none text-secondary-700 lg:text-4xl">{{ Money.format(configurationStore.calculatedPriceIncl, currency) }}</span>
                        <span class="pb-1 text-sm text-secondary-500">{{ label('site.order.incl_vat') }}</span>
                    </div>
                </div>
            </ConfigurationLoadingSkeleton>

            <div class="flex flex-row gap-x-4 md:mt-4 sticky md:static md:bottom-0 -bottom-6 bg-white md:py-0 py-2">
                <button type="button" @click="open.value = false"
                        class="md:block w-6/12 h-10 px-5 pointer rounded-xl font-medium flex items-center justify-center text-white bg-slate-700 hover:bg-slate-800 transition-all duration-500">
                    {{ label('site.cart.cancel') }}
                </button>
                <button @click="updateConfiguration()" type="button" class="h-10 px-5 w-6/12 pointer rounded-xl flex items-center justify-center text-white font-semibold bg-primary hover:bg-primary-300 transition-all duration-500 z-20">
                    {{ label('site.button.save') }}
                </button>
            </div>
        </template>
    </Modal>
</template>
